import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Shield,
  AlertCircle,
  Check,
  X,
  CheckSquare,
  ChevronDown,
  ChevronUp,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import DashboardPageLayout from './DashboardPageLayout';
import PageGuide from './PageGuide';
import { GUIDE_CONTENT } from '../config/guideContent';
import EmptyState from './EmptyState';
import { useRoles } from '../hooks/useData';
import { PrimaryButton, SecondaryButton, DangerButton, SuccessButton } from './Buttons';
import StandardModal from './StandardModal';
import InfoCard from './InfoCard';
import MassActionToolbar from './MassActionToolbar';
import CardListViewToggle from './CardListViewToggle';
import ResourceCard from './ResourceCard';
import { getAllTabIds } from '../config/navigation';

const PRESET_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#14B8A6', // Teal
  '#F97316', // Orange
];

const RolesTab = () => {
  const { roles, isLoading, refresh } = useRoles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [massActionMode, setMassActionMode] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [viewMode, setViewMode] = useState("card"); // Default to card view
  const [showStepGuide, setShowStepGuide] = useState(() => {
    return localStorage.getItem('hideStepGuide') !== 'true';
  });

  // Listen for guide visibility changes
  useEffect(() => {
    const handleGuideChange = () => {
      setShowStepGuide(localStorage.getItem('hideStepGuide') !== 'true');
    };
    window.addEventListener('guideVisibilityChanged', handleGuideChange);
    return () => window.removeEventListener('guideVisibilityChanged', handleGuideChange);
  }, []);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    permissions: [],
    tabPermissions: {}
  });
  
  const [modalStep, setModalStep] = useState(1); // 1 = Basic Info, 2 = Tab Permissions

  const handleOpenDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        name: role.name,
        description: role.description || '',
        color: role.color || '#3B82F6',
        permissions: role.permissions || ['view_bookings'],
        tabPermissions: role.tabPermissions || {}
      });
    } else {
      setEditingRole(null);
      setFormData({
        name: '',
        description: '',
        color: '#3B82F6',
        permissions: ['view_bookings'],
        tabPermissions: {}
      });
    }
    setDialogOpen(true);
    setModalStep(1); // Start at step 1
  };

  const handleSaveRole = async () => {
    console.log('🔧 handleSaveRole called');
    console.log('Form data:', formData);
    console.log('Editing role:', editingRole);
    
    if (!formData.name.trim()) {
      toast.error('Role name is required');
      return;
    }

    try {
      console.log('📤 Payload:', formData);
      
      if (editingRole) {
        // Update existing role
        console.log(`📡 PUT /roles/${editingRole.id}`);
        const response = await api.put(`/roles/${editingRole.id}`, formData);
        console.log('✅ Response:', response.data);
        toast.success(`Role "${formData.name}" updated successfully!`);
      } else {
        // Create new role
        console.log('📡 POST /roles');
        const response = await api.post('/roles', formData);
        console.log('✅ Response:', response.data);
        toast.success(`Role "${formData.name}" created successfully!`);
      }
      
      setDialogOpen(false);
      refresh(); // Use SWR mutate instead of fetchRoles
    } catch (error) {
      console.error('Error saving role:', error);
      toast.error(error.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;

    try {
      await api.delete(`/roles/${roleToDelete.id}`);
      toast.success(`Role "${roleToDelete.name}" deleted successfully!`);
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
      refresh(); // Use SWR mutate
    } catch (error) {
      console.error('Error deleting role:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const openDeleteDialog = (role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const toggleMassActionMode = () => {
    setMassActionMode(!massActionMode);
    if (massActionMode) {
      setSelectedRoles([]); // Clear selections when turning off
    }
  };

  if (isLoading) {
    return (
      <DashboardPageLayout
        title="Roles & Positions"
        subtitle="Define custom roles for your teamMembers"
      >
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-3" />
          <div className="text-gray-500">Loading roles...</div>
        </div>
      </DashboardPageLayout>
    );
  }

  return (
    <DashboardPageLayout
      title="Roles & Positions"
      subtitle="Define team positions"
      guideChildren={
        showStepGuide && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 w-full">
            <div className="flex-1">
              <div className="flex items-start sm:items-center gap-3">
                {roles.length > 0 ? (
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    ✓
                  </div>
                ) : (
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    1
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900">
                    {GUIDE_CONTENT.roles.title}
                  </h3>
                  <p className="text-sm text-gray-700 mt-1">
                    {GUIDE_CONTENT.roles.dynamicDescription(roles.length)}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowStepGuide(false);
                  localStorage.setItem('hideStepGuide', 'true');
                  window.dispatchEvent(new Event('guideVisibilityChanged'));
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
                <span className="hidden sm:inline ml-1">Dismiss</span>
              </Button>
              {roles.length > 0 && (
                <SecondaryButton 
                  onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'teamMembers' }))}
                  size="sm"
                >
                  Next: Add Team →
                </SecondaryButton>
              )}
            </div>
          </div>
        )
      }
      actions={
        <div className="flex items-center gap-2 w-full">
          {/* Mass Actions - Icon only, same size as view toggle */}
          <Button 
            onClick={toggleMassActionMode}
            variant="outline"
            size="sm"
            className={`h-9 w-9 p-0 flex items-center justify-center ${
              massActionMode ? 'bg-blue-50 border-blue-500 text-blue-600' : ''
            }`}
          >
            <CheckSquare className="w-4 h-4" />
          </Button>
          
          {/* Card/List view toggle */}
          <CardListViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
          
          {/* Create button - takes remaining space */}
          <PrimaryButton onClick={() => handleOpenDialog()} className="h-9 flex-1 sm:flex-none sm:px-4">
            <Plus className="w-4 h-4 mr-2" />
            Create
          </PrimaryButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Mass Action Toolbar */}
        {massActionMode && (
          <MassActionToolbar
            selectedCount={selectedRoles.length}
            onSelectAll={() => setSelectedRoles(roles.map(r => r.id))}
            onDeselectAll={() => setSelectedRoles([])}
            onDelete={async () => {
              if (window.confirm(`Delete ${selectedRoles.length} role(s)? This cannot be undone.`)) {
                try {
                  await Promise.all(selectedRoles.map(id => api.delete(`/roles/${id}`)));
                  toast.success(`Deleted ${selectedRoles.length} role(s)`);
                  setSelectedRoles([]);
                  refresh();
                } catch (error) {
                  toast.error('Failed to delete some roles');
                }
              }
            }}
            itemName="role"
          />
        )}

        {/* Roles Display - Card or List View */}
        {roles.length === 0 ? (
          <EmptyState
            icon={Shield}
            title="No Roles Created Yet"
            description="Create your first role to start organizing your team"
            actionLabel="Create First Role"
            onAction={() => handleOpenDialog()}
          />
        ) : viewMode === "card" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roles.map((role) => {
              const isSelected = selectedRoles.includes(role.id);
              return (
                <ResourceCard
                  key={role.id}
                  id={role.id}
                  name={role.name}
                  icon={Shield}
                  iconColor={role.color || '#3B82F6'}
                  primaryBadge={{
                    label: 'Active',
                    color: role.color,
                    variant: 'outline',
                    className: 'mt-1'
                  }}
                  description={role.description || 'No description provided'}
                  leftBorderColors={[role.color || '#3B82F6']}
                  massActionMode={massActionMode}
                  isSelected={isSelected}
                  onToggleSelect={(e) => {
                    if (e.target.checked) {
                      setSelectedRoles([...selectedRoles, role.id]);
                    } else {
                      setSelectedRoles(selectedRoles.filter(id => id !== role.id));
                    }
                  }}
                  onEdit={() => handleOpenDialog(role)}
                  onDelete={() => openDeleteDialog(role)}
                />
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {massActionMode && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                      <input
                        type="checkbox"
                        checked={selectedRoles.length === roles.length && roles.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRoles(roles.map(r => r.id));
                          } else {
                            setSelectedRoles([]);
                          }
                        }}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {roles.map((role) => {
                  const isSelected = selectedRoles.includes(role.id);
                  return (
                  <tr 
                    key={role.id} 
                    className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                  >
                    {massActionMode && (
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedRoles.includes(role.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRoles([...selectedRoles, role.id]);
                            } else {
                              setSelectedRoles(selectedRoles.filter(id => id !== role.id));
                            }
                          }}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${role.color}20`, border: `2px solid ${role.color}` }}
                        >
                          <Shield className="w-5 h-5" style={{ color: role.color }} />
                        </div>
                        <span className="font-medium text-gray-900">{role.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">
                        {role.description || 'No description provided'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge 
                        variant="outline"
                        style={{ borderColor: role.color, color: role.color }}
                      >
                        Active
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(role)}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(role)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Create/Edit Role Modal */}
        <StandardModal
          isOpen={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setModalStep(1);
          }}
          title={editingRole ? 'Edit Role' : 'Create New Role'}
          description={modalStep === 1 ? 'Step 1: Basic role information' : 'Step 2: Select which tabs this role can access'}
          size="sm"
          footer={
            <>
              {modalStep === 2 && (
                <SecondaryButton
                  onClick={() => setModalStep(1)}
                  className="flex-1"
                >
                  Back
                </SecondaryButton>
              )}
              {modalStep === 1 ? (
                <>
                  <SecondaryButton
                    onClick={() => setDialogOpen(false)}
                    className="flex-1"
                  >
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton
                    onClick={() => setModalStep(2)}
                    className="flex-1"
                    disabled={!formData.name.trim()}
                  >
                    Next
                  </PrimaryButton>
                </>
              ) : (
                <SuccessButton
                  onClick={handleSaveRole}
                  className="flex-1"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {editingRole ? 'Update Role' : 'Create Role'}
                </SuccessButton>
              )}
            </>
          }
        >
          {/* Tab Selector (Only for Editing) */}
          {editingRole && (
            <div className="flex gap-1 mb-4 border-b border-gray-200">
              <button
                type="button"
                onClick={() => setModalStep(1)}
                className={`flex-1 py-2 px-4 text-sm font-medium transition ${
                  modalStep === 1
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Role
              </button>
              <button
                type="button"
                onClick={() => setModalStep(2)}
                className={`flex-1 py-2 px-4 text-sm font-medium transition ${
                  modalStep === 2
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Permissions
              </button>
            </div>
          )}
          
          {/* Step 1: Basic Info */}
          {modalStep === 1 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Role Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Captain, Check-In Staff, Manager"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="description">Description (Optional)</Label>
                <Input
                  id="description"
                  placeholder="Brief description of this role"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Color</Label>
                <div className="flex gap-2 flex-wrap mt-2 p-1">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-10 h-10 rounded-lg border-2 transition-all ${
                        formData.color === color ? 'ring-2 ring-offset-2 ring-blue-500' : ''
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Tab Permissions */}
          {modalStep === 2 && (
            <div className="space-y-4">
              <div className="max-h-96 overflow-y-auto space-y-3 bg-gray-50 p-4 rounded-lg">
                {getAllTabIds().map(tab => (
                  <div key={tab.id} className="flex items-center space-x-3 p-2 hover:bg-white rounded transition">
                    <Checkbox
                      id={`tab-${tab.id}`}
                      checked={formData.tabPermissions?.[tab.id] === true}
                      onCheckedChange={(checked) => {
                        setFormData({
                          ...formData,
                          tabPermissions: {
                            ...formData.tabPermissions,
                            [tab.id]: checked
                          }
                        });
                      }}
                    />
                    <label
                      htmlFor={`tab-${tab.id}`}
                      className="flex-1 cursor-pointer"
                    >
                      <p className="text-sm font-medium text-gray-900">{tab.label}</p>
                      {tab.group && (
                        <p className="text-xs text-gray-500">{tab.group} section</p>
                      )}
                    </label>
                  </div>
                ))}
              </div>
              
              <div className="bg-gray-100 rounded-lg p-3">
                <p className="text-xs text-gray-600 text-center">
                  <strong>{Object.keys(formData.tabPermissions || {}).filter(k => formData.tabPermissions[k]).length}</strong> tabs will be visible to this role
                </p>
              </div>
            </div>
          )}
        </StandardModal>

        {/* Delete Confirmation Modal */}
        <StandardModal
          isOpen={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          title="Delete Role?"
          description={`Are you sure you want to delete the role "${roleToDelete?.name}"?`}
          size="sm"
          footer={
            <>
              <SecondaryButton
                onClick={() => setDeleteDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </SecondaryButton>
              <DangerButton
                onClick={handleDeleteRole}
                className="flex-1"
              >
                Delete Role
              </DangerButton>
            </>
          }
        >
          <InfoCard variant="danger" showIcon={true}>
            <p className="font-medium mb-1">This action cannot be undone.</p>
            <p>This role cannot be deleted if it's assigned to teamMembers or required by assets.</p>
          </InfoCard>
        </StandardModal>
      </div>
    </DashboardPageLayout>
  );
};

export default RolesTab;